import {useState, useCallback} from 'react';
import {FormattedMessage} from 'react-intl';
import {useMutation, type ApolloCache} from '@apollo/client';
import {CREATE_BOOKMARK_FOLDER_MUTATION, BOOKMARK_FOLDER_FRAGMENT, type CreateBookmarkFolderOutput} from 'graphql/admin/bookmarks';
import {DocumentType} from 'types';
import {useSelectedLinkContext} from 'contexts/SelectedLinkContext';
import {getErrorMessage} from 'utils';
import {useSnackbar} from 'hooks';
import CreateFolderDialog from './CreateFolderDialog';
import { Button } from '@/components/ui/atoms/Button/Button';
import { Add } from '@mui/icons-material';

const updateFoldersCache = (cache: ApolloCache<any>, {data} : {data: CreateBookmarkFolderOutput}) => {
  const createdFolder = data?.createFolder?.folder;
  if (createdFolder) cache.modify({
    fields: {
      adminFoldersByFolderType({folders: existingFoldersRefs = [], ...adminFoldersByFolderType}) {
        const createdFolderCacheRef = cache.writeFragment({
          data: createdFolder,
          fragment: BOOKMARK_FOLDER_FRAGMENT,
          fragmentName: 'BookmarkFolder'
        });
        return {
          ...adminFoldersByFolderType,
          folders: [...existingFoldersRefs, createdFolderCacheRef]
        }
      }
    }
  });
};

const CreateFolderButton = () : JSX.Element => {
  const [open, setOpen] = useState(false);

  const {successSnackbar} = useSnackbar();

  const {selectedLink: {channelKey: channel}} = useSelectedLinkContext();

  const onCompleted = (data : CreateBookmarkFolderOutput) => {
    const createdFolder = data?.createFolder?.folder;
    if (createdFolder) {
      successSnackbar(<FormattedMessage id="admin.bookmarks.folder.create.success" values={{ folderName: createdFolder.name }} />);
      handleClose();
    }
  }
  
  const [createFolder, {loading, data, error, reset}] = useMutation<CreateBookmarkFolderOutput>(CREATE_BOOKMARK_FOLDER_MUTATION, { update: updateFoldersCache, onCompleted });
  
  const handleClick = () => setOpen(true);

  const handleClose = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  const handleCreateFolder = ({folderName}) => createFolder({
    variables: {
      createFolderInput: {
        channel,
        folderName,
        folderType: DocumentType.BOOKMARK,
        otherWebUserIds: []
      }
    }
  });

  return <>
    <Button onClick={handleClick}>
      <Add />
      <FormattedMessage id="admin.bookmarks.button.createFolder" />
    </Button>
    <CreateFolderDialog
      errorReason={data?.createFolder?.errorReason || getErrorMessage(error)}
      open={open}
      onClose={handleClose}
      submitting={loading}
      handleCreateFolder={handleCreateFolder}
    />
  </>
}

export default CreateFolderButton;